import { useTranslation } from 'react-i18next';
import type { AlbumMap, AlbumMapStyle } from '../../types';
import { textOn, toneOn } from '../../utils/inkContrast';

interface Props {
  map: AlbumMap;
  style: AlbumMapStyle;
}

// In mm of the slot, so they print at this size: the legend ~9.5 pt, the pins' numbers ~9 pt.
// LEGEND_ROW is the server's LEGEND_ROW_MM, which sizes the band.
const PIN_H = 6.2;
const PIN_FONT = 3.2;
const LEGEND_FONT = 3.4;
const LEGEND_ROW = 5.6;
/** Roughly how wide a character of the legend runs, to cut a label at its column. */
const CHAR_MM = LEGEND_FONT * 0.55;

/**
 * ADR-0047: the album's map as the server drew it, in mm of its slot. Nothing
 * is projected here — the same numbers become the PDF.
 */
export default function AlbumMapView({ map, style }: Props) {
  const { t } = useTranslation();
  const tone = (i: number) => style.tones[i % style.tones.length];
  const mapH = map.legend ? map.legend.y : map.height;
  const rows = map.legend ? Math.ceil(map.legend.items.length / 2) : 0;
  const maxChars = Math.floor((map.width / 2 - 6) / CHAR_MM);

  return (
    <svg viewBox={`0 0 ${map.width} ${map.height}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet" role="img">
      {map.background === 'sea' ? (
        <>
          <rect width={map.width} height={map.height} fill={style.paper} />
          <rect width={map.width} height={mapH} fill={style.sea} />
          <path d={map.land} fill={style.land} stroke={style.coast} strokeWidth={0.25} strokeLinejoin="round" />
        </>
      ) : (
        <path d={map.land} fill={style.land} stroke={style.tones[0]} strokeWidth={0.35} strokeLinejoin="round" />
      )}

      {map.routes.map((r, i) => r.kind === 'road' ? (
        <g key={i}>
          <path d={r.d} fill="none" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" opacity={0.9} />
          <path d={r.d} fill="none" stroke={tone(r.tone)} strokeWidth={0.8} strokeLinecap="round" strokeLinejoin="round" />
        </g>
      ) : (
        <path key={i} d={r.d} fill="none" stroke={tone(r.tone)} strokeWidth={0.5} strokeLinecap="round"
          strokeDasharray={r.kind === 'transfer' ? '0.6 1.2' : '1.2 1'} />
      ))}

      {map.pins.map((p, i) => {
        const c = tone(p.tone);
        if (p.label === null) {
          return <circle key={i} cx={p.x} cy={p.y} r={p.hasPhoto ? 1.3 : 0.9} fill={p.hasPhoto ? c : '#fff'} stroke={c} strokeWidth={0.4} />;
        }
        const w = Math.max(PIN_H, p.label.length * PIN_FONT * 0.62 + 3);
        return (
          <g key={i}>
            <rect x={p.x - w / 2} y={p.y - PIN_H / 2} width={w} height={PIN_H} rx={PIN_H / 2}
              fill={p.hasPhoto ? c : '#fff'} stroke={c} strokeWidth={0.5} />
            <text x={p.x} y={p.y + PIN_FONT * 0.36} fontSize={PIN_FONT} fontWeight={700} textAnchor="middle" fill={p.hasPhoto ? textOn(c) : toneOn(c, '#fff', textOn('#fff'))}
              fontFamily="Inter, Helvetica, sans-serif">
              {p.label}
            </text>
          </g>
        );
      })}

      {map.legend?.items.map((item, i) => {
        const col = i < rows ? 0 : 1;
        const x = 2 + col * (map.width / 2);
        const y = map.legend!.y + 5 + (i % rows) * LEGEND_ROW;
        const label = map.legend!.kind === 'days'
          ? [t('album.dayN', { n: item.n }), item.label].filter(Boolean).join(' · ')
          : item.label;
        return (
          <text key={i} x={x} y={y} fontSize={LEGEND_FONT} fill={style.ink} fontFamily="Inter, Helvetica, sans-serif">
            {map.legend!.kind === 'days'
              ? <tspan fill={toneOn(tone(item.tone), style.paper, style.ink)} fontWeight={700}>●</tspan>
              : <tspan fill={toneOn(tone(item.tone), style.paper, style.ink)} fontWeight={700}>{item.n}</tspan>}
            <tspan dx={1.5}>{label.length > maxChars ? `${label.slice(0, maxChars - 1)}…` : label}</tspan>
          </text>
        );
      })}
    </svg>
  );
}
